import { z } from 'zod';

/**
 * Zod schema for individual calendar event from backend API
 * Validates the structure returned by CalendarEventsResponse
 */
export const BackendCalendarEventSchema = z.object({
    id: z.string(),
    title: z.string(),
    start_date: z.string(),
    start_time: z.string().nullable(),
    start_datetime: z.string(),
    end_date: z.string(),
    end_time: z.string().nullable(),
    end_datetime: z.string(),
    timezone: z.string().optional(),
    all_day: z.boolean(),
    description: z.string(),
    color: z.string(),
    text_color: z.string(),
    calendar_id: z.number(),
    calendar_name: z.string(),
    feed_id: z.number().nullable().optional(),
    feed_source: z.string().nullable().optional(),
    address: z.string().nullable().optional(),
    link: z.string().nullable().optional(),
    event_url: z.string(),
});

/**
 * Zod schema for calendar events response (nested in API response)
 * Contains array of events, site timezone, and optional error message
 */
export const CalendarEventsResponseSchema = z.object({
    events: z.array(BackendCalendarEventSchema),
    site_timezone: z.string(),
    error: z.string(),
});

/**
 * Zod schema for top-level API response
 * Contains success flag and data object
 */
export const CalendarApiResponseSchema = z.object({
    success: z.boolean(),
    error: z.string().optional().nullable(),
    data: CalendarEventsResponseSchema,
});

/**
 * TypeScript type inferred from BackendCalendarEventSchema
 */
export type BackendCalendarEvent = z.infer<typeof BackendCalendarEventSchema>;

/**
 * TypeScript type inferred from CalendarEventsResponseSchema
 */
export type CalendarEventsResponse = z.infer<typeof CalendarEventsResponseSchema>;

/**
 * TypeScript type inferred from CalendarApiResponseSchema
 */
export type CalendarApiResponse = z.infer<typeof CalendarApiResponseSchema>;

/**
 * Result type for fetching events, includes both events and site timezone
 */
export interface FetchEventsResult {
    events: BackendCalendarEvent[];
    siteTimezone: string;
}

// @see: src/Legacy/CalendarEvents/Enum/CalendarStyle.php
export const CALENDAR_STYLE_MAP: Record<string, number> = {
    FOUR_EVENTS: 0,
    SIX_EVENTS: 1,
    FOUR_EVENTS_FULL_CALENDAR: 2,
    FOUR_EVENTS_FACEBOOK_FEED: 3,
    FULL_CALENDAR: 4,
    GRID: 5,
} as const;

// @see: src/Legacy/CalendarEvents/Enum/ComponentBodyPosition.php
export const COMPONENT_BODY_POSITION_MAP = {
    NONE: 0,
    LEFT: 1,
    RIGHT: 2,
} as const;

export const DATE_SHAPE_MAP = {
    RECTANGLE: 0,
    CIRCLE: 1,
} as const;

/**
 * Zod schema for view data events sliders
 *
 * @see EventsSliderModel::stackItemSettings() in PHP
 */
export const ViewDataEventsSlidersSchema = z.object({
    // Stack / component metadata
    pageAccessID: z.string(),
    itemID: z.number(),
    componentTitle: z.string(),
    componentIntro: z.string(),
    editable: z.boolean(),
    isStackItemVisible: z.boolean(),
    hasBackgroundBreakout: z.boolean(),
    hasContentBreakout: z.boolean(),
    hasStackBackground: z.boolean(),

    // Calendar settings
    showCalendar: z.boolean(),
    calendarID: z.number(),
    calendarApiID: z.number(),
    calendarLink: z.string(),
    calendarStyle: z.number(),
    /** @see ComponentHeadingAlignment in PHP */
    componentHeadingAlignment: z.number(),
    gridTopBandShowImageOverlay: z.boolean(),
    hasGridTopBg: z.boolean(),
    hasGridBottomBg: z.boolean(),
    numberOfEvents: z.number(),
    standaloneCalendar: z.boolean().nullable(),
    calendarDateShape: z.number(), // 0 => rectangle, 1 => circle
    isMealCalendar: z.boolean(),

    // Component body
    hasComponentBody: z.boolean(),
    componentBodyPosition: z.number(), // 0 => none, 1 => left, 2 => right
    componentBodyContent: z.string(),
    componentBodyHeading: z.string().nullable(),
    useDefaultComponentHeadingColors: z.boolean(),

    // Facebook feed
    facebookFeedURL: z.string().nullable(),
    facebookFeedHeading: z.string().nullable(),

    // CSS variables — background
    '--ss-calendar-events-background-color': z.string(),
    '--ss-calendar-events-background-image': z.string(),
    '--ss-calendar-events-background-opacity': z.string(),

    // CSS variables — heading colors
    '--ss-calendar-events-optional-heading-color': z.string(),
    '--ss-calendar-events-optional-heading-accent-line-color': z.string(),

    // CSS variables — event date
    '--ss-calendar-events-event-date-color': z.string(),
    '--ss-calendar-events-event-date-bg-color': z.string(),
    '--ss-calendar-events-event-date-font-family': z.string(),
    '--ss-calendar-events-event-date-font-weight': z.string(),
    '--ss-calendar-events-event-date-font-style': z.string().or(z.boolean().transform((v) => v ? 'italic' : 'normal')),

    // CSS variables — event content / title
    '--ss-calendar-events-event-content-color': z.string(),
    '--ss-calendar-events-event-time-color': z.string(),
    /** Grid: overrides --event-time-color on event cards when set (never pure white on white panel). */
    '--ss-calendar-events-grid-event-details-time-color': z.string(),
    '--ss-calendar-events-event-title-font-family': z.string(),
    '--ss-calendar-events-event-title-font-weight': z.string(),
    '--ss-calendar-events-event-title-font-style': z.string().or(z.boolean().transform((v) => v ? 'italic' : 'normal')),

    // CSS variables — component intro
    '--ss-calendar-events-component-intro-text-color': z.string().optional(),
    '--ss-calendar-events-component-intro-link-color': z.string().optional(),

    // CSS variables — calendar link
    '--ss-calendar-events-calendar-link-color': z.string(),
    '--ss-calendar-events-calendar-link-background-color': z.string(),
    '--ss-calendar-events-calendar-link-font-family': z.string(),
    '--ss-calendar-events-calendar-link-font-weight': z.string(),
    '--ss-calendar-events-calendar-link-font-size': z.string(),

    // Grid band backgrounds (@see EventsSliderModel::stackItemSettings)
    '--ss-calendar-events-grid-top-bg-color': z.string(),
    '--ss-calendar-events-grid-bottom-bg-color': z.string(),
});

export type ViewDataEventsSlider = z.infer<typeof ViewDataEventsSlidersSchema>;
